<script setup lang="ts">
import { computed, ref } from 'vue';
import { paymentsApi, type Payment } from '@/api/payments';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// Pagos' bulk send: the payments email of the period to every selected
// caregiver, after a confirmation. The server queues one email per caregiver
// and leaves out those without an email.
const props = withDefaults(defineProps<{
  payments: Payment[];
  fromDate: string;
  toDate: string;
  disabled?: boolean;
}>(), { disabled: false });

const emit = defineEmits<{ sent: [] }>();

const showDialog = ref(false);
const isSending = ref(false);
const resultMessage = ref('');
const showResult = ref(false);
const showError = ref(false);

const recipientsCount = computed(() => props.payments.filter((payment) => payment.caregiver.email).length);
const withoutEmailCount = computed(() => props.payments.length - recipientsCount.value);

function localizedDate(date: string) {
  return date.split('-').reverse().join('/');
}

function professionals(count: number) {
  return count === 1 ? '1 profesional' : `${count} profesionales`;
}

function describeResult(queued: number, skippedWithoutEmail: number) {
  const sending = queued === 1 ? 'Se está enviando 1 correo de pago' : `Se están enviando ${queued} correos de pago`;
  if (!skippedWithoutEmail) return `${sending}.`;

  return `${sending}; ${professionals(skippedWithoutEmail)} sin correo quedaron fuera.`;
}

async function send() {
  isSending.value = true;
  showError.value = false;
  try {
    const { queued, skippedWithoutEmail } = await paymentsApi.sendEmails({
      caregiverIds: props.payments.map((payment) => payment.caregiver.id),
      fromDate: props.fromDate,
      toDate: props.toDate,
    });
    resultMessage.value = describeResult(queued, skippedWithoutEmail);
    showResult.value = true;
    showDialog.value = false;
    emit('sent');
  } catch {
    showError.value = true;
  } finally {
    isSending.value = false;
  }
}
</script>
<template>
  <v-btn
    class="mb-6 mr-2"
    color="primary"
    variant="tonal"
    prepend-icon="mdi-email-multiple"
    data-test="open-payment-emails"
    :disabled="disabled || payments.length === 0"
    @click="showDialog = true"
  >
    Enviar correos ({{ payments.length }})
  </v-btn>

  <responsive-dialog
    v-model="showDialog"
    max-width="600"
  >
    <v-card>
      <v-card-title class="ml-2 align-baseline d-flex">
        Enviar correos de pago
        <v-spacer />
        <v-btn
          icon="mdi-window-close"
          variant="plain"
          aria-label="Cerrar"
          @click="showDialog = false"
        />
      </v-card-title>
      <v-card-text data-test="payment-emails-summary">
        Se enviará el correo de pago desde el <b>{{ localizedDate(fromDate) }}</b>
        hasta el <b>{{ localizedDate(toDate) }}</b> a <b>{{ professionals(recipientsCount) }}</b>,
        cada uno a su correo.
      </v-card-text>
      <v-card-text
        v-if="withoutEmailCount"
        data-test="payment-emails-without-email"
      >
        <b>{{ professionals(withoutEmailCount) }}</b> sin correo no recibirán nada.
      </v-card-text>
      <v-card-text class="text-medium-emphasis">
        Los correos se envían en segundo plano y pueden tardar unos minutos.
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text="Cancelar"
          @click="showDialog = false"
        />
        <v-btn
          color="primary"
          data-test="confirm-payment-emails"
          :text="`Enviar ${recipientsCount === 1 ? '1 correo' : `${recipientsCount} correos`}`"
          :loading="isSending"
          :disabled="recipientsCount === 0"
          @click="send"
        />
      </v-card-actions>
    </v-card>
  </responsive-dialog>

  <v-snackbar
    v-model="showResult"
    :timeout="4000"
    color="primary"
    rounded="pill"
  >
    {{ resultMessage }}
  </v-snackbar>
  <v-snackbar
    v-model="showError"
    :timeout="4000"
    color="error"
    rounded="pill"
  >
    No se pudieron enviar los correos. Por favor, inténtalo nuevamente.
  </v-snackbar>
</template>
